import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";
import {
  hexWorldXZ,
  neighborsTorus as hexNeighborsTorus,
} from "../_lib/hex";

/**
 * Resonance — territory area-control, redesigned for 3D depth + engagement.
 * Game 12 of 12.
 *
 * Scoring contract preserved: 4-neighbor toroidal grid + tap claims +
 * 8s decay + cluster multiplier. Tap event list (t, cell_idx) replays
 * unchanged. v5(a) adds an opt-in hex 6-neighbor toroidal topology via
 * `?hex=1`; default ('square') leaves the live leaderboard untouched.
 */

export const GRID_W = 11;
export const GRID_H = 9;
export const N_CELLS = GRID_W * GRID_H;
export const DECAY_MS = 8000;
export const RUN_MS = 90_000;
const CELL_SIZE = 1.7;
const CELL_GAP = 0.12;
const RESONANCE_PULSE_PERIOD_MS = 22_000;
// v4 — AI rival mirror of server-side constants.
const AI_TICK_INTERVAL = 24;
const AI_TICK_MS = 50;
const AI_CLAIM_PERIOD_MS = AI_TICK_INTERVAL * AI_TICK_MS; // 1200ms
const AI_DECAY_MS = DECAY_MS;

export type ResonanceKind = "square" | "hex";

export function aiCellAt(seed: number, ai_idx: number): number {
  let s = (seed ^ (ai_idx * 0x9e3779b1)) | 0;
  s = Math.imul(s ^ (s >>> 15), 2246822507);
  s = Math.imul(s ^ (s >>> 13), 3266489909);
  return ((s ^ (s >>> 16)) >>> 0) % N_CELLS;
}

export interface ResonanceTap { t: number; cell_idx: number }

export interface ResonanceCallbacks {
  onHud: (controlled: number, cluster: number, scoreEstimate: number, aiControlled: number) => void;
  onGameOver: (result: {
    taps: ResonanceTap[];
    scoreEstimate: number;
    peakControlled: number;
    peakCluster: number;
    aiPeakControlled: number;
    kind: ResonanceKind;
    /** v5(d): ms from page load (performance.timeOrigin) to first
     *  tap; null if the player never tapped. Sent to /api/runs/
     *  resonance as time_to_first_tap_ms per dossier §3 Q10. */
    firstTapMs: number | null;
  }) => void;
}

interface BiomePalette { dim: number; active: number; emissive: number; sky: number; accent: number; }
const PALETTES: BiomePalette[] = [
  { dim: 0x0a0e22, active: 0x46ffe0, emissive: 0x00aa90, sky: 0x040614, accent: 0x66ddff },
  { dim: 0x1a0822, active: 0xff3df0, emissive: 0xcc1aa0, sky: 0x140520, accent: 0xff77ff },
  { dim: 0x221a08, active: 0xffd54a, emissive: 0xff8800, sky: 0x14100a, accent: 0xffee99 },
];

function squareNeighborsTorus(idx: number): number[] {
  const q = idx % GRID_W;
  const r = Math.floor(idx / GRID_W);
  return [
    ((q + 1) % GRID_W) + r * GRID_W,
    ((q - 1 + GRID_W) % GRID_W) + r * GRID_W,
    q + ((r + 1) % GRID_H) * GRID_W,
    q + ((r - 1 + GRID_H) % GRID_H) * GRID_W,
  ];
}

function neighborsFor(kind: ResonanceKind, idx: number): number[] {
  return kind === "hex"
    ? hexNeighborsTorus(idx, GRID_W, GRID_H)
    : squareNeighborsTorus(idx);
}

interface CellVisual { mesh: THREE.Mesh; mat: THREE.MeshStandardMaterial; baseY: number; isShrine: boolean; }

const N_SHRINES_CLIENT = 5;

/** Mirror of scoring-resonance.ts shrineIndices — must use the same RNG
 *  so the player sees the cells the server will score as shrines. */
export function clientShrineIndices(seed: number): Set<number> {
  const out = new Set<number>();
  let s = seed | 0;
  const rng = () => {
    s = (s + 0x9e3779b9) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  while (out.size < N_SHRINES_CLIENT) {
    const idx = 1 + Math.floor(rng() * (N_CELLS - 1));
    out.add(idx);
  }
  return out;
}

export class Resonance {
  private engine: Engine;
  private audio = new AudioBed();
  private cells: CellVisual[] = [];
  private claimedAt = new Float32Array(N_CELLS);
  private aiClaimedAt = new Float32Array(N_CELLS);
  private aiClaimIdx = 0;
  private taps: ResonanceTap[] = [];
  private startedAt = 0;
  private scoreEstimate = 0;
  private lastScoreT = 0;
  private peakControlled = 0;
  private peakCluster = 0;
  private aiPeakControlled = 0;
  private gameOver = false;
  private cb: ResonanceCallbacks;
  private canvas: HTMLCanvasElement;
  private currentBiome = 0;
  private targetBiome = 0;
  private biomeBlend = 0;
  private envLight: THREE.PointLight;
  private resonancePulse = 0;
  private lastPulseT = 0;
  private seed: number;
  private kind: ResonanceKind;
  /** v5(c) — cluster-glow audio state. The chime stem swells in three
   *  tiers as the player's largest cluster crosses 10 / 25 / 50 cells.
   *  Tracked so we only call setChimeGain on tier change, not every tick. */
  private clusterAudioTier = 0;

  /** v6 — three parallax starfield layers (near / mid / far). The
   *  tilted top-down camera looks up-and-forward at the grid, so stars
   *  biased above the grid plane sit in the sky portion of the frame.
   *  Per memory feedback-sibling-games-shallow §"real 3D scene with
   *  depth cues." */
  private starLayers: THREE.Points[] = [];

  private onPointer = (e: PointerEvent) => this.handleTap(e);

  private shrines: Set<number>;

  constructor(
    canvas: HTMLCanvasElement,
    cb: ResonanceCallbacks,
    seed = Math.floor(Math.random() * 0x7fffffff),
    kind: ResonanceKind = "square",
  ) {
    this.canvas = canvas;
    this.cb = cb;
    this.seed = seed;
    this.kind = kind;
    this.shrines = clientShrineIndices(seed);
    this.claimedAt.fill(-Infinity);
    this.aiClaimedAt.fill(-Infinity);

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    const gridW = GRID_W * (CELL_SIZE + CELL_GAP);
    this.engine.camera.position.set(0, -gridW * 0.8, gridW * 0.95);
    this.engine.camera.lookAt(0, 2, 0);
    this.engine.camera.fov = 56;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(PALETTES[0].sky, 18, 50);
    this.engine.scene.background = new THREE.Color(PALETTES[0].sky);

    const ambient = new THREE.AmbientLight(0x506080, 0.4);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 0.9);
    key.position.set(8, 14, 12);
    this.engine.scene.add(key);
    const fill = new THREE.DirectionalLight(0x4060ff, 0.4);
    fill.position.set(-8, 6, 8);
    this.engine.scene.add(fill);
    this.envLight = new THREE.PointLight(0x66ddff, 0.5, 24, 1.5);
    this.envLight.position.set(0, 4, 0);
    this.engine.scene.add(this.envLight);

    const floorGeo = new THREE.PlaneGeometry(gridW * 2.2, gridW * 2.2);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x040814, roughness: 0.9, metalness: 0.15 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.5;
    this.engine.scene.add(floor);

    this.buildGrid();

    // v6 — parallax starfield. Three THREE.Points layers at different
    // Z depths give the play space genuine 3D distance behind the
    // grid. Stars don't move per-frame (no GPU cost beyond initial
    // draw). The grid sits at z=0 and the camera looks up-forward
    // from (0, -gridW*0.8, gridW*0.95), so stars are biased to Y > 0
    // (the "sky" half of the frame) and to large negative Z (past
    // the grid in the camera's look direction). Materials set
    // `fog: false` so the layers persist past the Fog(18, 50) ramp.
    // Per memory feedback-sibling-games-shallow §"real 3D scene with
    // depth cues."
    const makeStars = (
      count: number,
      z: number,
      spread: { x: number; y: number },
      opts: { size: number; color: number },
    ) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * spread.x * 2;
        // Bias upward — most stars sit above the grid plane (y=0) so
        // they read as sky, not under the floor mesh at y=-0.5.
        positions[i * 3 + 1] = (r() - 0.15) * spread.y;
        positions[i * 3 + 2] = z + (r() - 0.5) * 12;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(520, -180, { x: 220, y: 120 }, { size: 0.85, color: 0x7a82a8 }); // far cool
    makeStars(260,  -90, { x: 130, y: 80 },  { size: 0.7,  color: 0xa0a8c8 }); // mid
    makeStars(110,  -45, { x: 80,  y: 50 },  { size: 0.55, color: 0xffe0c0 }); // near warm

    canvas.addEventListener("pointerdown", this.onPointer);
  }

  private cellWorldPos(idx: number): { x: number; z: number } {
    if (this.kind === "hex") {
      const q = idx % GRID_W;
      const r = Math.floor(idx / GRID_W);
      return hexWorldXZ(q, r, GRID_W, GRID_H, CELL_SIZE + CELL_GAP);
    }
    const q = idx % GRID_W;
    const r = Math.floor(idx / GRID_W);
    const step = CELL_SIZE + CELL_GAP;
    const offsetX = (GRID_W - 1) / 2;
    const offsetZ = (GRID_H - 1) / 2;
    return { x: (q - offsetX) * step, z: (r - offsetZ) * step };
  }

  private buildGrid() {
    const palette = PALETTES[0];
    const isHex = this.kind === "hex";
    for (let i = 0; i < N_CELLS; i++) {
      const { x, z } = this.cellWorldPos(i);
      const isShrine = this.shrines.has(i);
      const h = isShrine ? 1.0 : 0.4;
      // Hex cells render as 6-sided cylinders; square stays box.
      const geo = isHex
        ? new THREE.CylinderGeometry(CELL_SIZE / 2, CELL_SIZE / 2, h, 6)
        : new THREE.BoxGeometry(CELL_SIZE, h, CELL_SIZE);
      const mat = new THREE.MeshStandardMaterial({
        color: isShrine ? 0xffd54a : palette.dim,
        emissive: isShrine ? 0xff8800 : palette.dim,
        emissiveIntensity: isShrine ? 1.0 : 0.4,
        roughness: 0.55,
        metalness: 0.35,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.set(x, isShrine ? 0.3 : 0, z);
      this.engine.scene.add(m);
      this.cells.push({ mesh: m, mat, baseY: isShrine ? 0.3 : 0, isShrine });
    }
  }

  private handleTap(e: PointerEvent) {
    if (this.gameOver) return;
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    const ndc = new THREE.Vector3(nx, ny, 0.5).unproject(this.engine.camera);
    const dir = ndc.sub(this.engine.camera.position).normalize();
    const dist = -this.engine.camera.position.y / dir.y;
    const wx = this.engine.camera.position.x + dir.x * dist;
    const wz = this.engine.camera.position.z + dir.z * dist;

    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < N_CELLS; i++) {
      const { x, z } = this.cellWorldPos(i);
      const d = (wx - x) ** 2 + (wz - z) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0 || bestD > (CELL_SIZE * 1.2) ** 2) return;

    const t = Math.round(performance.now() - this.startedAt);
    this.taps.push({ t, cell_idx: best });
    this.claimedAt[best] = t;
    this.aiClaimedAt[best] = -Infinity;
    for (const n of neighborsFor(this.kind, best)) {
      if (t - this.claimedAt[n] < DECAY_MS) this.claimedAt[n] = t;
    }
    this.audio.chime(7, 0);
  }

  /** v4 — mirror of server AI step. Called once per AI tick interval to
   *  advance the deterministic AI rival schedule. */
  private aiStep(now: number) {
    let attempts = 0;
    let aiTarget = aiCellAt(this.seed, this.aiClaimIdx);
    while (attempts < N_CELLS && now - this.claimedAt[aiTarget] < DECAY_MS) {
      this.aiClaimIdx += 1;
      aiTarget = aiCellAt(this.seed, this.aiClaimIdx);
      attempts += 1;
    }
    this.aiClaimedAt[aiTarget] = now;
    this.aiClaimIdx += 1;
  }

  private currentAiControlled(now: number): number {
    let c = 0;
    for (let i = 0; i < N_CELLS; i++) if (now - this.aiClaimedAt[i] < AI_DECAY_MS) c += 1;
    return c;
  }

  private currentControlled(now: number): number {
    let c = 0;
    for (let i = 0; i < N_CELLS; i++) if (now - this.claimedAt[i] < DECAY_MS) c += 1;
    return c;
  }

  private currentLargestCluster(now: number): number {
    const visited = new Uint8Array(N_CELLS);
    let best = 0;
    for (let start = 0; start < N_CELLS; start++) {
      if (visited[start] || now - this.claimedAt[start] >= DECAY_MS) continue;
      let size = 0;
      const queue = [start];
      visited[start] = 1;
      while (queue.length) {
        const i = queue.shift()!;
        size += 1;
        for (const n of neighborsFor(this.kind, i)) {
          if (!visited[n] && now - this.claimedAt[n] < DECAY_MS) {
            visited[n] = 1;
            queue.push(n);
          }
        }
      }
      if (size > best) best = size;
    }
    return best;
  }

  private renderCells(now: number, palette: BiomePalette, blend: number, prev: BiomePalette) {
    const active = new THREE.Color(palette.active).lerp(new THREE.Color(prev.active), 1 - blend);
    const emissive = new THREE.Color(palette.emissive).lerp(new THREE.Color(prev.emissive), 1 - blend);
    const dim = new THREE.Color(palette.dim).lerp(new THREE.Color(prev.dim), 1 - blend);
    const aiColor = new THREE.Color(0xc24bff);
    const aiEmissive = new THREE.Color(0x7a1ec8);
    for (let i = 0; i < N_CELLS; i++) {
      const c = this.cells[i];
      const age = now - this.claimedAt[i];
      const aiAge = now - this.aiClaimedAt[i];
      const aiHeld = aiAge < AI_DECAY_MS && age >= DECAY_MS;
      if (age < DECAY_MS) {
        const v = 1 - age / DECAY_MS;
        const targetY = c.baseY + 0.4 + v * 0.9 + this.resonancePulse * 0.4;
        c.mesh.position.y += (targetY - c.mesh.position.y) * 0.18;
        if (c.isShrine) {
          c.mat.emissiveIntensity = 1.5 + v * 1.2 + this.resonancePulse * 0.6;
        } else {
          c.mat.color.lerpColors(dim, active, v);
          c.mat.emissive.lerpColors(dim, emissive, v);
          c.mat.emissiveIntensity = 0.5 + v * 1.4 + this.resonancePulse * 0.6;
        }
      } else if (aiHeld) {
        const v = 1 - aiAge / AI_DECAY_MS;
        const targetY = c.baseY + 0.25 + v * 0.55;
        c.mesh.position.y += (targetY - c.mesh.position.y) * 0.18;
        if (!c.isShrine) {
          c.mat.color.lerpColors(dim, aiColor, v);
          c.mat.emissive.lerpColors(dim, aiEmissive, v);
        }
        c.mat.emissiveIntensity = 0.5 + v * 1.1 + this.resonancePulse * 0.5;
      } else {
        c.mesh.position.y += (c.baseY - c.mesh.position.y) * 0.12;
        if (!c.isShrine) {
          c.mat.color.copy(dim);
          c.mat.emissive.copy(dim);
        }
        c.mat.emissiveIntensity = c.isShrine
          ? 0.8 + this.resonancePulse * 0.5
          : 0.3 + this.resonancePulse * 0.4;
      }
    }
  }

  private frame(dt: number, _t: number) {
    if (this.gameOver) return;
    const now = performance.now() - this.startedAt;
    if (now > RUN_MS) { this.finish(); return; }

    const targetBiome = Math.floor(now / 30_000) % PALETTES.length;
    if (targetBiome !== this.targetBiome) {
      this.currentBiome = this.targetBiome;
      this.targetBiome = targetBiome;
      this.biomeBlend = 0;
    }
    if (this.currentBiome !== this.targetBiome) {
      this.biomeBlend = Math.min(1, this.biomeBlend + dt * 0.4);
      const a = PALETTES[this.currentBiome];
      const b = PALETTES[this.targetBiome];
      const sky = new THREE.Color(a.sky).lerp(new THREE.Color(b.sky), this.biomeBlend);
      (this.engine.scene.background as THREE.Color).copy(sky);
      if (this.engine.scene.fog instanceof THREE.Fog) this.engine.scene.fog.color.copy(sky);
      if (this.biomeBlend >= 1) this.currentBiome = this.targetBiome;
    }
    const currentPalette = PALETTES[this.targetBiome];
    const prevPalette = PALETTES[this.currentBiome];

    if (now - this.lastPulseT > RESONANCE_PULSE_PERIOD_MS) {
      this.resonancePulse = 1;
      this.lastPulseT = now;
      this.audio.chime(-5, 0);
    }
    if (this.resonancePulse > 0) this.resonancePulse = Math.max(0, this.resonancePulse - dt * 1.4);

    const aiClaimsDue = Math.floor(now / AI_CLAIM_PERIOD_MS);
    while (this.aiClaimIdx < aiClaimsDue) {
      this.aiStep(now);
    }

    this.renderCells(now, currentPalette, this.biomeBlend, prevPalette);

    // v6 — adaptive engagement signal drives both post-processing
    // boost (FOV pulse / afterimage / chromatic) and the audio drone
    // bed. peakControlled is monotonic across the run, so the ramp
    // doesn't drop on momentary decay — it reads as accumulated
    // territory. Normalized so a peak of ~50 cells (half the board)
    // saturates the signal; drone caps at 0.7. Per memory
    // feedback-sibling-games-shallow §"escalation hook".
    const engagement = Math.min(1, this.peakControlled / 50);
    this.engine.setBoost(engagement, dt);
    this.audio.setDroneIntensity(0.15 + engagement * 0.55);

    const tickElapsed = now - this.lastScoreT;
    if (tickElapsed >= 100) {
      const controlled = this.currentControlled(now);
      if (controlled > this.peakControlled) this.peakControlled = controlled;
      const cluster = controlled > 0 ? this.currentLargestCluster(now) : 0;
      if (cluster > this.peakCluster) this.peakCluster = cluster;
      const mult = Math.min(2.5, 1 + cluster * 0.02);
      this.scoreEstimate += 10 * controlled * mult * (tickElapsed / 1000);
      const aiControlled = this.currentAiControlled(now);
      if (aiControlled > this.aiPeakControlled) this.aiPeakControlled = aiControlled;
      this.cb.onHud(controlled, cluster, Math.round(this.scoreEstimate), aiControlled);
      this.lastScoreT = now;
      this.envLight.intensity = 0.5 + cluster * 0.15;
      this.envLight.color.setHex(currentPalette.accent);
      // v5(c) — cluster-glow audio. Swell the sustained chime in three
      // tiers (10 / 25 / 50). Each tier corresponds to a louder chord
      // bed; long-ramp smoothing so the swell reads as a crescendo
      // rather than a step.
      const tier = cluster >= 50 ? 3 : cluster >= 25 ? 2 : cluster >= 10 ? 1 : 0;
      if (tier !== this.clusterAudioTier) {
        const gainByTier = [0, 0.35, 0.65, 1.0];
        // Climbing tiers swell slowly (700ms); falling tiers decay
        // faster (350ms) so the player feels the loss snap to silence.
        const smoothMs = tier > this.clusterAudioTier ? 700 : 350;
        this.audio.setChimeGain(gainByTier[tier], smoothMs);
        this.clusterAudioTier = tier;
      }
    }
  }

  start() {
    this.audio.arm();
    // v6 — seed drone at baseline; per-frame updates in frame() ramp
    // it with peakControlled (replacing the fixed 0.4 baseline). Per
    // memory feedback-sibling-games-shallow §"escalation hook".
    this.audio.setDroneIntensity(0.15);
    // v5(c) — seed cluster-glow chime at silent; tiers crescendo in.
    this.audio.setChimeGain(0, 0);
    this.clusterAudioTier = 0;
    this.claimedAt.fill(-Infinity);
    this.aiClaimedAt.fill(-Infinity);
    this.aiClaimIdx = 0;
    this.taps = [];
    this.scoreEstimate = 0;
    this.lastScoreT = 0;
    this.peakControlled = 0;
    this.peakCluster = 0;
    this.aiPeakControlled = 0;
    this.startedAt = performance.now();
    this.gameOver = false;
    this.currentBiome = 0;
    this.targetBiome = 0;
    this.biomeBlend = 0;
    this.resonancePulse = 0;
    this.lastPulseT = 0;
    this.engine.start();
  }

  private finish() {
    this.gameOver = true;
    this.audio.setDroneIntensity(0);
    this.audio.setChimeGain(0, 500);
    // v5(d): firstTapMs measured against performance.now() (= ms
    // since timeOrigin = page load). taps[].t is relative to
    // this.startedAt, so the absolute page-load offset is
    // startedAt + taps[0].t.
    const firstTapMs = this.taps.length > 0 ? Math.round(this.startedAt + this.taps[0].t) : null;
    this.cb.onGameOver({
      taps: this.taps,
      scoreEstimate: Math.round(this.scoreEstimate),
      peakControlled: this.peakControlled,
      peakCluster: this.peakCluster,
      aiPeakControlled: this.aiPeakControlled,
      kind: this.kind,
      firstTapMs,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    this.canvas.removeEventListener("pointerdown", this.onPointer);
    // v6 — release the adaptive drone hold so a navigate-away → mount
    // cycle doesn't bleed audio. AudioBed's own decay handles the rest.
    this.audio.setDroneIntensity(0);
    // v6 — drop parallax-starfield layers off the scene + dispose
    // their GPU resources so re-mounting doesn't accumulate
    // THREE.Points.
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    this.engine.dispose();
  }
}
